import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';

import { EMPLOYEE_VARIANCE_MANAGEMENT } from '../../../shared/constants/api-routes.constants';
import type { TableQuery } from '../../../shared/components/generic-table/models/table-query.model';
import { HttpClientService } from '../../../core/services/http-client.service';
import { OrganizationService } from '../../../core/services/organization.service';
import type {
  ActionAlertStatus,
  CaseInvestigationTrackerApiDto,
  CaseInvestigationTrackerRow,
} from '../models/case-investigation-tracker-row.model';
import type {
  CaseInvestigationTrackerClient,
  TableDataResponse,
} from '../data-access/case-investigation-tracker-client.token';
import { filterCaseInvestigationTrackerRowsBySearch } from '../utils/case-investigation-tracker-search.utils';

function mapDtoToRow(dto: CaseInvestigationTrackerApiDto): CaseInvestigationTrackerRow {
  const actionAlert: ActionAlertStatus = dto.attentionRequired ? 'attention' : 'none';
  return {
    id: String(dto.caseId),
    caseId: dto.caseId,
    actionAlert,
    caseStatus: dto.caseStatus as CaseInvestigationTrackerRow['caseStatus'],
    employeeStatus: dto.employeeStatus as CaseInvestigationTrackerRow['employeeStatus'],
    employee: dto.employeeName,
    employeeId: dto.employeeId,
    jobRole: dto.jobRole,
    createdDateTime: dto.createdAt ?? '',
    createdBy: dto.createdBy,
    lastEditDate: dto.lastEditDate ? dto.lastEditDate.replace(/-/g, '/') : '',
    lastEditTime: dto.lastEditTime,
    editedBy: dto.editedBy,
  };
}

/**
 * Real API client for Case Investigation Tracker.
 * Fetches from GET employee-variance-management/users?organizationId= and applies client-side paging/sort/filter.
 */
@Injectable({ providedIn: 'root' })
export class CaseInvestigationTrackerService implements CaseInvestigationTrackerClient {
  private readonly http = inject(HttpClientService);
  private readonly organizationService = inject(OrganizationService);

  getRows(query: TableQuery): Observable<TableDataResponse<CaseInvestigationTrackerRow>> {
    const organizationId = this.organizationService.organizationIdParam();
    const period = query.filters?.['period'];
    const daysBack =
      period === 'last7Days' ? 7 : period === 'last30Days' ? 30 : undefined;
    const params: Record<string, string> = { organizationId };
    if (daysBack !== undefined) params['daysBack'] = String(daysBack);
    return this.http
      .get<CaseInvestigationTrackerApiDto[]>(EMPLOYEE_VARIANCE_MANAGEMENT.USERS(), {
        params,
      })
      .pipe(
        map((dtos) => {
          const allRows = dtos.map(mapDtoToRow);
          // Only apply column filters (table header filters); exclude filter-bar keys like 'period'
          const columnFilters = this.getColumnOnlyFilters(query.filters);
          const normalized = {
            page: Math.max(0, query.page ?? 0),
            rows: Math.max(1, query.rows ?? 10),
            search: query.search,
            sortField: query.sortField,
            sortOrder: query.sortOrder,
          };
          const afterSearch = filterCaseInvestigationTrackerRowsBySearch(allRows, normalized.search);
          const afterFilters = this.applyColumnFilters(afterSearch, columnFilters);
          const sorted = this.applySort(
            afterFilters,
            normalized.sortField,
            normalized.sortOrder
          );
          const start = normalized.page * normalized.rows;
          const pageData = sorted.slice(start, start + normalized.rows);
          return { data: pageData, totalRecords: sorted.length };
        }),
        catchError(() => of({ data: [], totalRecords: 0 }))
      );
  }

  /** Filter-bar keys (e.g. period) are not row properties; exclude them from column filtering. */
  private static readonly FILTER_BAR_KEYS = new Set<string>(['period']);

  private getColumnOnlyFilters(filters: Record<string, string> | undefined): Record<string, string> | undefined {
    if (!filters || Object.keys(filters).length === 0) return undefined;
    const columnOnly: Record<string, string> = {};
    for (const [key, value] of Object.entries(filters)) {
      if (CaseInvestigationTrackerService.FILTER_BAR_KEYS.has(key)) continue;
      columnOnly[key] = value;
    }
    return Object.keys(columnOnly).length === 0 ? undefined : columnOnly;
  }

  private applyColumnFilters(
    rows: readonly CaseInvestigationTrackerRow[],
    filters: Record<string, string> | undefined
  ): CaseInvestigationTrackerRow[] {
    if (!filters || Object.keys(filters).length === 0) return [...rows];
    return rows.filter((row) =>
      Object.entries(filters).every(([field, filterValue]) => {
        const term = (filterValue ?? '').trim().toLowerCase();
        if (!term) return true;
        const cell = row[field];
        const cellStr = cell != null ? String(cell).toLowerCase() : '';
        return cellStr.includes(term);
      })
    );
  }

  private applySort(
    rows: CaseInvestigationTrackerRow[],
    sortField: string | undefined,
    sortOrder: 1 | -1 | undefined
  ): CaseInvestigationTrackerRow[] {
    if (!sortField) return rows;
    const order: 1 | -1 = sortOrder === -1 ? -1 : 1;
    return [...rows].sort((a, b) => {
      const av = a[sortField];
      const bv = b[sortField];
      if (av == null && bv == null) return 0;
      if (av == null) return -1 * order;
      if (bv == null) return 1 * order;
      return String(av).localeCompare(String(bv)) * order;
    });
  }
}
